/* eslint no-underscore-dangle: 0 */

import React, { createRef, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { displayErrorToast, displaySuccessToast } from 'helpers/toaster';
import { CUSTOMER_REVIEW_MAX_IMAGES, CUSTOMER_REVIEW_MIN_IMAGES } from 'constants/index';
import { submitCustomerReview } from '../actions/orderActions';

const ACCEPTED_TYPES = 'image/png, image/jpeg, image/jpg, image/webp';
const MAX_FILE_SIZE_MB = 10;
const BYTES_IN_MB = 1024 * 1024;

/**
 * Made to order: the seller uploads the actual product images so the customer
 * can review them. The order cannot be accepted until the customer approves.
 */
const MadeToOrderUploadModal = ({ show, order, onHide = () => {}, onComplete = () => {} }) => {
  const [images, setImages] = useState([]);
  const [waitingResponse, setWaitingResponse] = useState(false);
  const hiddenFileInput = useRef([createRef()]);

  const remainingSlots = CUSTOMER_REVIEW_MAX_IMAGES - images.length;

  const handleSelectFiles = () => {
    hiddenFileInput.current.click();
  };

  const onChangeFile = ({ target }) => {
    const selected = Array.from(target.files || []);
    const tooLarge = selected.filter((file) => file.size > MAX_FILE_SIZE_MB * BYTES_IN_MB);
    if (tooLarge.length) {
      displayErrorToast(`Each photo must be smaller than ${MAX_FILE_SIZE_MB}MB.`);
    }
    const accepted = selected.filter((file) => file.size <= MAX_FILE_SIZE_MB * BYTES_IN_MB).slice(0, remainingSlots);
    if (accepted.length) {
      setImages((prev) => prev.concat(accepted.map((file) => ({ file, preview: URL.createObjectURL(file) }))));
    }
    // Let the same file be picked again after it is removed.
    target.value = '';
  };

  const removeImage = (removeIndex) => {
    setImages((prev) => {
      const removed = prev[removeIndex];
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
      return prev.filter((item, index) => index !== removeIndex);
    });
  };

  const handleClose = () => {
    images.forEach((image) => image.preview && URL.revokeObjectURL(image.preview));
    setImages([]);
    onHide();
  };

  const handleSubmit = async () => {
    setWaitingResponse(true);
    try {
      const result = await submitCustomerReview({
        params: {
          orderId: order.order_id,
          orderReference: order.reference_no,
          customerReviewImages: images.map((image) => ({ name: 'Product Image', file: image.file })),
        },
      });
      if (result?.success) {
        displaySuccessToast(result.message);
        handleClose();
        onComplete();
      } else {
        displayErrorToast(result?.message || 'Something Went Wrong!');
      }
    } catch (e) {
      displayErrorToast(e?.message);
    }
    setWaitingResponse(false);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add Product Photos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted text-small">
          This is a made to order product. Upload up to {CUSTOMER_REVIEW_MAX_IMAGES} photos of the actual product. The customer has to approve them before the
          order can be accepted.
        </p>
        <Button size="sm" variant="outline-primary" disabled={remainingSlots <= 0} onClick={handleSelectFiles}>
          <CsLineIcons icon="upload" size={14} /> Select Photos
        </Button>
        <input type="file" multiple accept={ACCEPTED_TYPES} className="d-none" onChange={onChangeFile} ref={hiddenFileInput} />
        <If condition={images.length}>
          <div className="d-flex flex-wrap gap-2 mt-3">
            {images.map((image, index) => (
              <div key={image.preview} className="position-relative">
                <img src={image.preview} className="img-fluid rounded-md" alt={image.file?.name} width="100" height="100" />
                <Button
                  size="sm"
                  variant="danger"
                  className="btn-icon btn-icon-only position-absolute top-0 end-0"
                  onClick={() => removeImage(index)}
                  title="Remove"
                >
                  <CsLineIcons icon="close" size={12} />
                </Button>
              </div>
            ))}
          </div>
        </If>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="success" disabled={waitingResponse || images.length < CUSTOMER_REVIEW_MIN_IMAGES} onClick={handleSubmit}>
          <If condition={waitingResponse}>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
          </If>
          Submit For Customer Review
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MadeToOrderUploadModal;
